import { useCallback, useState } from "react"

import type { SalesCustomer } from "../types"

import { useGetSalesCustomers } from "./useGetSalesCustomers"

interface UseCustomerSearchReturn {
  searchInputValue: string
  setSearchInputValue: (value: string) => void
  customers: SalesCustomer[]
  isFetching: boolean
  showSearchResults: boolean
  selectedCustomer: SalesCustomer | null
  setSelectedCustomer: (customer: SalesCustomer | null) => void
  handleSearch: (value: string) => void
  handleCustomerSuggestionSelect: (customer: SalesCustomer) => void
  handleResultsScroll: (e: React.UIEvent<HTMLDivElement>) => void
}

export const useCustomerSearch = (): UseCustomerSearchReturn => {
  const [searchInputValue, setSearchInputValue] = useState("")
  const [submittedQuery, setSubmittedQuery] = useState("")
  const [selectedCustomer, setSelectedCustomer] = useState<SalesCustomer | null>(null)

  const { customers, isFetching, loadMore } = useGetSalesCustomers(submittedQuery)

  const showSearchResults = submittedQuery.length >= 2

  const handleResultsScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      if (!isFetching && scrollHeight - scrollTop - clientHeight <= 200) {
        loadMore()
      }
    },
    [isFetching, loadMore]
  )

  const handleSearch = (value: string) => {
    if (value.trim().length < 2) return
    setSubmittedQuery(value.trim())
    setSelectedCustomer(null)
  }

  const handleCustomerSuggestionSelect = (customer: SalesCustomer) => {
    setSearchInputValue(customer.company.name)
    handleSearch(customer.company.name)
  }

  return {
    searchInputValue,
    setSearchInputValue,
    customers,
    isFetching,
    showSearchResults,
    selectedCustomer,
    setSelectedCustomer,
    handleSearch,
    handleCustomerSuggestionSelect,
    handleResultsScroll,
  }
}
